/**
 * A trip kept while the game is closed, and carried on when it is opened
 * again: as far along the way as the time away would have taken it.
 *
 * Saved every few seconds while the autopilot travels — the route, how far
 * along it, the vehicle and its tank, and when — to the browser's storage.
 * On the next visit the time since is driven "off screen" at the roads' own
 * speeds (see `advanceAlong`), burning fuel as it goes; the game starts at
 * that point, in that vehicle, still on its way. Arrived meanwhile, it starts
 * at the destination. Run dry meanwhile, it starts where the tank did.
 */

import type { VehicleDamage } from '../core/vehicle/damage.js';
import type { Tank } from '../core/economy/energy.js';
import type { Travel } from '../core/nav/autopilot.js';
import type { RoutePoint } from '../core/nav/route.js';
import { advanceAlong, makeRoute, pointAt, routeToGeo } from '../core/nav/route.js';
import { geoToEnu, makeOrigin } from '../core/geo/enu.js';

const KEY = 'wsg.trip';
/** A trip left longer than this is let go rather than resumed, ms. */
const KEEP_FOR = 7 * 24 * 3_600_000;
/** Off screen, the helicopter cruises, and a runner and a rider keep their pace, m/s. */
const PACE: Record<Exclude<Travel, 'car'>, number> = { heli: 45, foot: 6, moto: 9 };

export interface SavedTrip {
  v: 1;
  travel: Travel;
  to: RoutePoint;
  /** Where it was when saved: the helicopter's start for the straight line on. */
  at: RoutePoint;
  /** The route, as it was being followed; empty for the helicopter. */
  points: RoutePoint[];
  speeds: number[];
  junctions: Array<{ lat: number; lon: number; stop: boolean }>;
  along: number;
  limits: boolean;
  /** Date.now() when saved. */
  savedAt: number;
  /** The vehicle it was in: its kind, its paint, its tank, and what crashes did to it. */
  vehicle?: { species: string; paint: number; tank: Tank; damage?: VehicleDamage };
}

export function saveTrip(trip: SavedTrip): void {
  try { localStorage.setItem(KEY, JSON.stringify(trip)); } catch { /* not kept */ }
}

export function clearTrip(): void {
  try { localStorage.removeItem(KEY); } catch { /* nothing to clear */ }
}

export function loadTrip(now = Date.now()): SavedTrip | undefined {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return undefined;
    const t = JSON.parse(raw) as SavedTrip;
    if (t.v !== 1 || now - t.savedAt > KEEP_FOR) { clearTrip(); return undefined; }
    return t;
  } catch {
    return undefined;
  }
}

/** Where the trip has got to after the time away, and in what state. */
export interface Resumed {
  trip: SavedTrip;
  /** Where to start, facing which way (compass degrees). */
  lat: number;
  lon: number;
  heading: number;
  /** How far along the route; for the helicopter, metres flown towards the destination. */
  along: number;
  /** Metres covered while away. */
  covered: number;
  arrived: boolean;
  /** The tank ran dry on the way: stopped there. */
  dry: boolean;
}

/** Carry the trip on by the time since it was saved. */
export function resumeTrip(trip: SavedTrip, now = Date.now()): Resumed {
  const seconds = Math.max(0, (now - trip.savedAt) / 1000);
  const tank = trip.vehicle?.tank;
  // How far the tank still goes, metres: the helicopter burns by the hour, the rest by the kilometre.
  const reach = tank === undefined || tank.use <= 0 ? Infinity
    : trip.travel === 'heli' ? (tank.level / tank.use) * 3600 * PACE.heli
      : (tank.level / tank.use) * 100_000;

  if (trip.travel === 'heli' || trip.points.length < 2) {
    const o = makeOrigin(trip.at.lat, trip.at.lon);
    const to = geoToEnu(o, trip.to.lat, trip.to.lon);
    const total = Math.hypot(to.east, to.north);
    const pace = trip.travel === 'car' ? 13 : PACE[trip.travel];
    const covered = Math.min(total, seconds * pace, reach);
    const k = total > 0 ? covered / total : 1;
    const route = makeRoute([trip.at, trip.to]);
    const p = routeToGeo(route, to.east * k, to.north * k);
    burn(trip, covered, seconds);
    return {
      trip, lat: p.lat, lon: p.lon, heading: ((Math.atan2(to.east, to.north) * 180) / Math.PI + 360) % 360,
      along: covered, covered, arrived: covered >= total - 1, dry: covered < total - 1 && covered >= reach,
    };
  }

  const route = makeRoute(trip.points, trip.speeds, trip.junctions);
  const pace = trip.travel === 'car' ? undefined : PACE[trip.travel];
  let along = pace === undefined ? advanceAlong(route, trip.along, seconds) : Math.min(route.length, trip.along + pace * seconds);
  const dry = along - trip.along > reach;
  if (dry) along = trip.along + reach;
  const covered = along - trip.along;
  burn(trip, covered, seconds);
  const p = pointAt(route, along);
  const geo = routeToGeo(route, p.east, p.north);
  return {
    trip, lat: geo.lat, lon: geo.lon, heading: ((Math.atan2(p.de, p.dn) * 180) / Math.PI + 360) % 360,
    along, covered, arrived: along >= route.length - 3, dry,
  };
}

/** Take what the time away used out of the tank. */
function burn(trip: SavedTrip, metres: number, seconds: number): void {
  const tank = trip.vehicle?.tank;
  if (tank === undefined) return;
  const used = trip.travel === 'heli' ? (tank.use * Math.min(seconds, (metres / PACE.heli) + 60)) / 3600 : (tank.use * metres) / 100_000;
  tank.level = Math.max(0, tank.level - used);
}
